/**
 * Per-turn "Copy as markdown" (client-side, free): one answer, in a form
 * you can paste into a PR description, a doc or a message.
 *
 * Round 3 (owner): "why only copy text? It should copy as markdown so any
 * answer can be included." So a diagram is no longer dropped on the floor —
 * it copies as a fenced ```mermaid block under its title, which GitHub,
 * Notion and every markdown previewer renders as the picture itself.
 *
 * What stays out: the tool activity (stage directions, not the answer), the
 * model's thinking (round 5 — that is the guide working the answer out, and
 * pasting it into a PR description would be quoting somebody's notes as
 * their conclusion) and the cost footer (our plumbing). The provenance layer
 * lives in the session export, which is a different, paid, whole-session
 * artifact.
 *
 * The loop below is an ALLOW list on purpose: a block kind it has never
 * heard of copies as nothing, so the next frame type the server invents
 * cannot leak into somebody's document by default.
 */

import type { TurnBlock } from './transcript';

/** Flatten one turn's blocks to markdown: prose plus diagrams as fences. */
export function turnCopyMarkdown(blocks: TurnBlock[]): string {
  const parts: string[] = [];
  for (const block of blocks) {
    if (block.kind === 'prose') {
      const text = block.text.trim();
      if (text.length > 0) parts.push(text);
      continue;
    }
    if (block.kind === 'diagram') {
      const title = block.data.title.trim();
      const mermaid = block.data.mermaid.trim();
      if (mermaid.length === 0) continue;
      parts.push(
        `${title.length > 0 ? `**${title}**\n\n` : ''}\`\`\`mermaid\n${mermaid}\n\`\`\``,
      );
    }
  }
  return parts.join('\n\n');
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
